export type Rgb = [number, number, number];

/** One grain of sand, with fixed per-grain randomness so every frame is deterministic. */
export interface ThinkingOrbGrain {
  y0: number;
  th: number;
  h: number;
  grain: number;
  tone: number;
  drift: number;
  rough: number;
  pack: number;
}

/** Where the loop is at one moment: how merged the dots are, how far the breath has filled the globe. */
export interface ThinkingOrbPhase {
  /** 0 = three dots in a row, 1 = one dot at the center */
  merged: number;
  /** 1 = the typing bounce has settled */
  still: number;
  revolving: boolean;
  orbitTurn: number;
  orbitTilt: number;
  /** 0..1 radius of the carousel, as a share of the dot gap */
  orbitOpen: number;
  spread: number;
  /** 0 = all the sand in the dot, 1 = all of it on the globe */
  breath: number;
  swell: number;
  /** 1 while the globe is formed: the thinking light, rings and wind belong to this */
  whole: number;
  /** horizontal position of the shared thinking light, in globe radii (off-globe when > 1.25) */
  bandX: number;
  /** how far the sand has settled into the gradient sphere */
  shade: number;
}

/**
 * Timing and math for the chat's thinking orb. The loop: three typing dots revolve on a carousel and
 * merge, breathe in to open into a globe of sand that settles into a gradient sphere inside armillary
 * rings, think, breathe out, and revolve back out into three dots.
 *
 * The "Thinking…" Shimmer beside the orb sweeps left to right every 2s, starting on mount like the
 * orb. The orb's light is the first half of that sweep, and the loop is five shimmer cycles, so the
 * two never drift apart.
 */
export class ThinkingOrbHelper {
  static readonly SHIMMER_S = 2;

  static readonly TIMING = {
    row: 1.3,
    merge: 1.0,
    inhale: 1.2,
    hold: 2.8,
    exhale: 1.2,
    part: 1.6,
    rowEnd: 1.3,
    overlap: 0.2,
  };

  static readonly MERGE_AT = ThinkingOrbHelper.TIMING.row;

  static readonly INHALE_AT =
    ThinkingOrbHelper.MERGE_AT +
    ThinkingOrbHelper.TIMING.merge -
    ThinkingOrbHelper.TIMING.overlap;

  static readonly HOLD_AT =
    ThinkingOrbHelper.INHALE_AT + ThinkingOrbHelper.TIMING.inhale;

  static readonly EXHALE_AT =
    ThinkingOrbHelper.HOLD_AT + ThinkingOrbHelper.TIMING.hold;

  static readonly PART_AT =
    ThinkingOrbHelper.EXHALE_AT +
    ThinkingOrbHelper.TIMING.exhale -
    ThinkingOrbHelper.TIMING.overlap;

  static readonly LOOP =
    ThinkingOrbHelper.PART_AT +
    ThinkingOrbHelper.TIMING.part +
    ThinkingOrbHelper.TIMING.rowEnd;

  /** Globe spin, rad/s. */
  static readonly SPIN = 0.45;

  static clamp01(x: number): number {
    return Math.min(1, Math.max(0, x));
  }

  static easeSine(u: number): number {
    return -(Math.cos(Math.PI * u) - 1) / 2;
  }

  static smooth(a: number, b: number, x: number): number {
    const t = ThinkingOrbHelper.clamp01((x - a) / (b - a));
    return t * t * (3 - 2 * t);
  }

  /** Deterministic 0..1 noise, so grains keep their place from frame to frame. */
  static hash(i: number, k: number): number {
    const v = Math.sin(i * 12.9898 + k * 78.233) * 43758.5453;
    return v - Math.floor(v);
  }

  /** Small orbs still need enough grains to read as sand, not dots. */
  static getGrainCount(size: number): number {
    return Math.round(Math.min(12000, size * size * 1.6 + 200));
  }

  static makeGrains(count: number): ThinkingOrbGrain[] {
    const { hash } = ThinkingOrbHelper;
    return Array.from({ length: count }, (_, i) => ({
      y0: 1 - 2 * hash(i, 1),
      th: hash(i, 2) * Math.PI * 2,
      h: hash(i, 3),
      grain: 0.45 + 0.9 * hash(i, 4) ** 2,
      tone: hash(i, 5),
      drift: 0.5 + hash(i, 6),
      rough: (hash(i, 7) - 0.5) * 0.05,
      pack: Math.sqrt(hash(i, 10)),
    }));
  }

  /** Reads a `#rrggbb` token value; falls back when the variable is missing. */
  static hexToRgb(hex: string, fallback: Rgb): Rgb {
    const match = hex.trim().match(/^#?([0-9a-f]{6})$/i);
    if (!match) return fallback;
    const channel = (i: number) => parseInt(match[1].slice(i, i + 2), 16);
    return [channel(0), channel(2), channel(4)];
  }

  /**
   * The state of the loop at time `T` (seconds into the loop); `t` is the running clock, used for
   * the shimmer-synced light.
   */
  static getPhase(T: number, t: number): ThinkingOrbPhase {
    const H = ThinkingOrbHelper;
    const { TIMING, MERGE_AT, INHALE_AT, HOLD_AT, EXHALE_AT, PART_AT } = H;
    const mergeEnd = MERGE_AT + TIMING.merge;
    const merging = T >= MERGE_AT && T < mergeEnd;
    const parting = T >= PART_AT && T < PART_AT + TIMING.part;

    let merged = 0;
    let still = 0;
    let orbitTurn = 0;
    let orbitTilt = 0;
    let orbitOpen = 0;
    let spread = 0;
    if (merging || parting) {
      // The row becomes a carousel seen from slightly above. Merging, the dots swing round a vertical
      // axis and spiral in; parting runs the same move outward, still turning the same way.
      const u = merging
        ? (T - MERGE_AT) / TIMING.merge
        : 1 - (T - PART_AT) / TIMING.part;
      const inward = merging
        ? H.easeSine(H.clamp01((u - 0.25) / 0.75))
        : H.easeSine(u);
      merged = inward;
      orbitOpen = 1 - inward;
      // 0.9 turn in and 1.1 more out: two whole turns, so the dots land exactly back in their row
      orbitTurn =
        2 *
        Math.PI *
        (merging ? 0.9 * H.easeSine(u) : 0.9 + 1.1 * (1 - H.easeSine(u)));
      orbitTilt = 0.7 * H.smooth(0, 0.4, u);
      spread = H.easeSine(H.clamp01(u / 0.5));
      still = merging ? H.easeSine(H.clamp01(u / 0.4)) : 1;
    } else if (T >= mergeEnd && T < PART_AT) {
      merged = 1;
      still = 1;
    } else if (T >= PART_AT + TIMING.part) {
      still = 1 - H.easeSine(H.clamp01((T - PART_AT - TIMING.part) / 0.4));
    }

    let breath = 0;
    if (T >= INHALE_AT && T < HOLD_AT) {
      breath = H.easeSine((T - INHALE_AT) / TIMING.inhale);
    } else if (T >= HOLD_AT && T < EXHALE_AT) {
      breath = 1;
    } else if (T >= EXHALE_AT && T < EXHALE_AT + TIMING.exhale) {
      breath = 1 - H.easeSine((T - EXHALE_AT) / TIMING.exhale);
    }

    // the shimmer's band enters the text at 1/6 of its cycle, so the orb's band crosses the orb over
    // the half-cycle just before that
    const cycle = ((t % H.SHIMMER_S) / H.SHIMMER_S + 1) % 1;
    const sweep = ((cycle + 1 / 3) % 1) / 0.5;

    return {
      merged,
      still,
      revolving: merging || parting,
      orbitTurn,
      orbitTilt,
      orbitOpen,
      spread,
      breath,
      swell: 1 + 0.035 * Math.sin(Math.PI * breath) * (T < HOLD_AT ? 1 : 0),
      whole: H.easeSine(
        H.clamp01(Math.min((T - HOLD_AT + 0.4) / 1.2, (EXHALE_AT - T) / 1.0))
      ),
      bandX: sweep <= 1 ? -1.25 + 2.5 * sweep : 9,
      // the gradient crossfades in over about a second as the sand arrives, and out as it leaves
      shade: Math.min(
        H.smooth(INHALE_AT + 0.55 * TIMING.inhale, HOLD_AT + 0.35, T),
        1 - H.smooth(EXHALE_AT - 0.3, EXHALE_AT + 0.45 * TIMING.exhale, T)
      ),
    };
  }
}
